import { supabase } from '@/lib/supabase';

const FAVORITES_STORAGE_KEY = 'ecodeal_favorites';
const MAX_FAVORITES = 10;

class FavoritesService {
  // 로컬 스토리지에서 즐겨찾기 목록 조회
  private getLocalFavorites(): string[] {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to get local favorites:', error);
      return [];
    }
  }

  // 로컬 스토리지에 즐겨찾기 목록 저장
  private setLocalFavorites(favoriteIds: string[]) {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favoriteIds));
    } catch (error) {
      console.error('Failed to set local favorites:', error);
    }
  }

  // 사용자의 즐겨찾기 목록 조회 (로그인 상태에 따라 다른 저장소 사용)
  async getUserFavorites(userId?: string | null) {
    try {
      if (userId) {
        // 로그인 상태: Supabase에서 조회
        const { data, error } = await supabase
          .from('favorites')
          .select('restaurant_id')
          .eq('user_id', userId);

        if (error) {
          console.error('Get user favorites error:', error);
          return { success: false, error: error.message };
        }

        const favoriteIds = data.map(item => item.restaurant_id);
        return { success: true, data: favoriteIds };
      } else {
        // 로그아웃 상태: localStorage에서 조회
        const favoriteIds = this.getLocalFavorites();
        return { success: true, data: favoriteIds };
      }
    } catch (error) {
      return { success: false, error: '즐겨찾기 목록을 불러오는 중 오류가 발생했습니다.' };
    }
  }

  // 즐겨찾기 추가
  async addToFavorites(userId: string | null, restaurantId: string) {
    try {
      if (userId) {
        // 로그인 상태: Supabase에 저장
        // 먼저 현재 즐겨찾기 개수 확인
        const { data: existing } = await supabase
          .from('favorites')
          .select('id')
          .eq('user_id', userId);

        if (existing && existing.length >= MAX_FAVORITES) {
          return { success: false, error: `즐겨찾기는 최대 ${MAX_FAVORITES}개까지만 등록할 수 있습니다.` };
        }

        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: userId,
            restaurant_id: restaurantId
          });

        if (error) {
          console.error('Add to favorites error:', error);
          return { success: false, error: error.message };
        }

        return { success: true };
      } else {
        // 로그아웃 상태: localStorage에 저장
        const favorites = this.getLocalFavorites();
        
        if (favorites.length >= MAX_FAVORITES) {
          return { success: false, error: `즐겨찾기는 최대 ${MAX_FAVORITES}개까지만 등록할 수 있습니다.` };
        }

        if (!favorites.includes(restaurantId)) {
          favorites.push(restaurantId);
          this.setLocalFavorites(favorites);
        }

        return { success: true };
      }
    } catch (error) {
      return { success: false, error: '즐겨찾기 추가 중 오류가 발생했습니다.' };
    }
  }

  // 즐겨찾기 제거
  async removeFromFavorites(userId: string | null, restaurantId: string) {
    try {
      if (userId) {
        // 로그인 상태: Supabase에서 제거
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', userId)
          .eq('restaurant_id', restaurantId);

        if (error) {
          console.error('Remove from favorites error:', error);
          return { success: false, error: error.message };
        }

        return { success: true };
      } else {
        // 로그아웃 상태: localStorage에서 제거
        const favorites = this.getLocalFavorites();
        const filtered = favorites.filter(id => id !== restaurantId);
        this.setLocalFavorites(filtered);

        return { success: true };
      }
    } catch (error) {
      return { success: false, error: '즐겨찾기 제거 중 오류가 발생했습니다.' };
    }
  }

  // 즐겨찾기 토글
  async toggleFavorite(userId: string | null, restaurantId: string) {
    try {
      const isFavResult = await this.isFavorite(userId, restaurantId);
      if (!isFavResult.success) {
        return isFavResult;
      }

      if (isFavResult.data) {
        // 이미 즐겨찾기에 있으면 제거
        return await this.removeFromFavorites(userId, restaurantId);
      } else {
        // 없으면 추가
        return await this.addToFavorites(userId, restaurantId);
      }
    } catch (error) {
      return { success: false, error: '즐겨찾기 처리 중 오류가 발생했습니다.' };
    }
  }

  // 특정 매장의 즐겨찾기 여부 확인
  async isFavorite(userId: string | null, restaurantId: string) {
    try {
      if (userId) {
        // 로그인 상태: Supabase에서 확인
        const { data, error } = await supabase
          .from('favorites')
          .select('id')
          .eq('user_id', userId)
          .eq('restaurant_id', restaurantId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') { // PGRST116은 row not found 에러
          console.error('Check favorite error:', error);
          return { success: false, error: error.message };
        }

        return { success: true, data: !!data };
      } else {
        // 로그아웃 상태: localStorage에서 확인
        const favorites = this.getLocalFavorites();
        return { success: true, data: favorites.includes(restaurantId) };
      }
    } catch (error) {
      return { success: false, error: '즐겨찾기 상태 확인 중 오류가 발생했습니다.' };
    }
  }

  // 로그인 시 localStorage의 즐겨찾기를 Supabase로 마이그레이션
  async migrateLocalFavoritesToSupabase(userId: string) {
    try {
      const localFavorites = this.getLocalFavorites();
      if (localFavorites.length === 0) {
        return { success: true };
      }

      // 기존 Supabase 즐겨찾기 조회
      const { data: existingFavorites } = await supabase
        .from('favorites')
        .select('restaurant_id')
        .eq('user_id', userId);

      const existingIds = existingFavorites?.map(f => f.restaurant_id) || [];
      
      // 새로 추가할 즐겨찾기 필터링
      let newFavorites = localFavorites.filter(id => !existingIds.includes(id));
      
      // 실제 존재하고 활성화된 매장만 필터링
      if (newFavorites.length > 0) {
        const { data: validRestaurants } = await supabase
          .from('restaurants')
          .select('id')
          .in('id', newFavorites)
          .eq('is_open', true);
        
        const validIds = validRestaurants?.map(r => r.id) || [];
        
        // 유효하지 않은 ID 로깅
        const invalidIds = newFavorites.filter(id => !validIds.includes(id));
        if (invalidIds.length > 0) {
          console.log('[Favorites] 삭제되거나 비활성화된 매장 제외:', invalidIds.length, '개');
        }
        
        // validIds로 newFavorites 교체
        newFavorites = validIds;
      }
      
      // 최대 개수 체크
      const totalCount = existingIds.length + newFavorites.length;
      if (totalCount > MAX_FAVORITES) {
        // 최대 개수를 초과하면 일부만 마이그레이션
        const availableSlots = MAX_FAVORITES - existingIds.length;
        newFavorites.splice(availableSlots);
      }

      if (newFavorites.length > 0) {
        // Supabase에 추가 (이제 모두 유효한 ID)
        const insertData = newFavorites.map(restaurantId => ({
          user_id: userId,
          restaurant_id: restaurantId
        }));

        const { error } = await supabase
          .from('favorites')
          .insert(insertData);

        if (error) {
          console.error('Migration error:', error);
          // 에러 발생해도 localStorage 정리
          localStorage.removeItem(FAVORITES_STORAGE_KEY);
          return { success: false, error: error.message };
        }
      }

      // 마이그레이션 완료 후 localStorage 정리
      localStorage.removeItem(FAVORITES_STORAGE_KEY);

      return { success: true, migrated: newFavorites.length };
    } catch (error) {
      console.error('Migration failed:', error);
      // 예외 발생해도 localStorage 정리
      localStorage.removeItem(FAVORITES_STORAGE_KEY);
      return { success: false, error: '즐겨찾기 마이그레이션 중 오류가 발생했습니다.' };
    }
  }

  // 즐겨찾기 매장 상세 정보 조회
  async getFavoriteRestaurants(userId: string | null) {
    try {
      const favoritesResult = await this.getUserFavorites(userId);
      if (!favoritesResult.success || !favoritesResult.data || !favoritesResult.data.length) {
        return { success: true, data: [] };
      }

      // 매장 정보 조회 (새로운 product_offers 구조 사용)
      const { data: restaurants, error } = await supabase
        .from('restaurants')
        .select(`
          id,
          name,
          bestmenu1,
          bestmenu2, 
          bestmenu3,
          pickup_instructions,
          is_open,
          is_new,
          store_type,
          image_url,
          rating,
          total_reviews,
          created_at,
          latitude,
          longitude,
          reservation_discount_enabled,
          deadline_discount_start_time,
          deadline_discount_end_time,
          products!inner (
            id,
            name,
            original_price,
            image_url,
            is_available,
            offers:product_offers(
              id,
              offer_type,
              discount_rate,
              discounted_price,
              min_discount_rate,
              is_random_set,
              pickup_start_time,
              pickup_end_time,
              reservation_available_end_time,
              reservation_pickup_start_time,
              reservation_pickup_end_time,
              deadline_pickup_start_time,
              deadline_pickup_end_time,
              deadline_order_start_time,
              deadline_order_end_time,
              max_quantity_per_order,
              is_active
            ),
            stock:product_stock(
              date,
              initial_stock,
              current_stock,
              sold_stock,
              version
            )
          )
        `)
        .in('id', favoritesResult.data || [])
        .eq('products.is_available', true)
        .eq('products.offers.is_active', true)
        .eq('products.stock.date', new Date().toISOString().split('T')[0]);

      if (error) {
        console.error('Get favorite restaurants error:', error);
        return { success: false, error: error.message };
      }

      // 데이터 구조 변환 (새로운 offers 구조)
      const restaurantsWithProducts = restaurants?.map(restaurant => {
        const bestMenuItems = [restaurant.bestmenu1, restaurant.bestmenu2, restaurant.bestmenu3]
          .filter(Boolean)
          .join(' | ');

        // 모든 상품의 모든 제안에서 가장 저렴한 것 찾기
        let lowestPriceOffer = null;
        let lowestPriceProduct = null;

        for (const product of restaurant.products) {
          for (const offer of product.offers || []) {
            if (offer.is_active && 
                (!lowestPriceOffer || offer.discounted_price < lowestPriceOffer.discounted_price)) {
              lowestPriceOffer = offer;
              lowestPriceProduct = product;
            }
          }
        }

        // 레거시 product 구조 생성 (하위 호환성)
        const legacyProduct = lowestPriceOffer && lowestPriceProduct ? {
          id: lowestPriceProduct.id,
          original_price: lowestPriceProduct.original_price,
          discounted_price: lowestPriceOffer.discounted_price,
          discount_rate: lowestPriceOffer.discount_rate,
          discount_type: lowestPriceOffer.offer_type
        } : null;

        return {
          ...restaurant,
          bestmenu: bestMenuItems,
          latitude: restaurant.latitude || 37.5665,
          longitude: restaurant.longitude || 126.978,
          product: legacyProduct,
          products: restaurant.products
        };
      }) || [];

      return { success: true, data: restaurantsWithProducts };
    } catch (error) {
      return { success: false, error: '즐겨찾기 매장 목록을 불러오는 중 오류가 발생했습니다.' };
    }
  }
}

export const favoritesService = new FavoritesService();